import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Wrench, Lightbulb, Activity, Building2, Home as HomeIcon, CheckCircle2, Clock, ThumbsUp, Star, Phone, Camera, FileText, DollarSign, Banknote, CreditCard, Timer, AlertTriangle } from 'lucide-react';
import { AnimatedSection, AnimatedGrid, AnimatedGridItem } from '../components/AnimatedSection';
export function Home() {
  return <div className="w-full" data-id="element-311" data-mp="24gikycywpant">
      {/* HERO SECTION */}
      <section className="relative bg-navy text-white overflow-hidden pt-[72px] border-b-4 border-accent" data-id="element-312" data-mp="24haeli8y0ybg">
        <div className="relative grid grid-cols-1 md:grid-cols-12 min-h-[560px] md:min-h-[620px]" data-id="element-313" data-mp="29i7zi2lc86gn">
          {/* Left — panel video */}
          <div className="md:col-span-7 relative h-72 md:h-auto z-10 shadow-2xl overflow-hidden" data-id="element-314" data-mp="3kvtnhkfgo20l">
            <video src="https://cdn.magicpatterns.com/uploads/vyd3LhuauEC5um1DD4PSex/20260617_114359249.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover object-left scale-[1.85] origin-left" data-id="element-315" data-mp="3owcejc9j541q" />
          </div>

          {/* Right — content on navy */}
          <div className="md:col-span-5 bg-navy relative flex items-center px-6 md:px-10 lg:px-14 py-12 md:py-16" data-id="element-316" data-mp="2mxa66hpm5ee3">
            <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-317" data-mp="2hy71iydjpa6n"></div>
            <AnimatedSection className="w-full max-w-xl relative z-10" data-id="element-318" data-mp="2d6xd213qgcwc">
              <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/40 px-3 py-1.5 rounded-sm mb-6" data-id="element-319" data-mp="288414d43gtya">
                <Zap className="w-4 h-4 text-yellow-400" fill="currentColor" data-id="element-320" data-mp="23a2fplj74ijn" />
                <span className="text-[11px] font-bold tracking-wider text-yellow-400 uppercase" data-id="element-321" data-mp="1z7xnmr8ebtz4">
                  Licensed & Insured Electrical Contractor
                </span>
              </div>
              <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight mb-6 uppercase" data-id="element-322" data-mp="1kk7f0qgov4dn">
                Powering Homes and Businesses with{' '}
                <span className="text-accent" data-id="element-323" data-mp="1amt0wxi8mroi">Safe, Reliable</span> Electrical
                Solutions
              </h1>
              <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed" data-id="element-324" data-mp="1fkul5lregx1k">
                Professional residential and commercial electrical services
                delivered with safety, quality workmanship, and customer
                satisfaction at the forefront.
              </p>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-325" data-mp="1tkdps4l05q94">
                <Link to="/quote" className="bg-accent hover:bg-accent-hover text-white px-6 py-3.5 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors text-center flex items-center justify-center gap-2 group" data-id="element-326" data-mp="1yki7fxxncr0x">
                  Request a Free Quote
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-327" data-mp="fl0sdueex0vz" />
                </Link>
                <a href="tel:4075040596" className="bg-transparent hover:bg-white/10 text-white border border-white/40 px-6 py-3.5 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors text-center flex items-center justify-center gap-2" data-id="element-328" data-mp="alxwp0kbo70o">
                  <Phone className="w-4 h-4" data-id="element-329" data-mp="3ssvrse0f31c1" />
                  Call Now
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FEATURED HIGHLIGHTS BAR — dark navy with circular icons */}
      <section className="bg-navy border-t border-slate-800/60" data-id="element-330" data-mp="o1oohmols6s">
        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-331" data-mp="qnwmidhi9pj">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-332" data-mp="1h1ruptxd2qj2">
            {[{
            title: 'Financing Available',
            icon: DollarSign
          }, {
            title: 'Emergency Service Available',
            icon: Clock
          }, {
            title: 'Residential & Commercial Services',
            icon: HomeIcon
          }, {
            title: 'Free Estimates',
            icon: FileText
          }].map((highlight, i) => <div key={i} className="flex items-center gap-4" data-id="element-333" data-mp="2qdajtkw9jdv8" data-mpi={i}>
                <div className="bg-accent w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 shadow-lg" data-id="element-334" data-mp="2qawp5d5hw3o7">
                  <highlight.icon className="w-6 h-6 md:w-7 md:h-7 text-white" data-id="element-335" data-mp="3020vjoh3ug7f" />
                </div>
                <span className="font-display font-bold text-white text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-336" data-mp="351ww1dqlfpi8">
                  {highlight.title}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SERVICES / RECENT PROJECTS */}
      <section className="py-16 md:py-20 bg-white" id="services" data-id="element-337" data-mp="12f8arzode0aa">
        <div className="max-w-7xl mx-auto px-6" data-id="element-338" data-mp="ydegc94rpsik">
          <AnimatedSection className="text-center mb-10" data-id="element-339" data-mp="ycljbzvm2hdm">
            <p className="text-sm font-bold tracking-widest text-accent uppercase mb-2" data-id="element-340" data-mp="te1rxjy90ddv">
              Services Offered / Recent Work
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy tracking-tight uppercase mb-4" data-id="element-341" data-mp="ogryzubnhjla">
              Real Jobs. Real Pictures. Real Success.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg" data-id="element-342" data-mp="1h4e9lmjapohn">
              We don't use stock photos. Explore our actual completed projects
              to see the quality and scope of the electrical services we
              provide.
            </p>
          </AnimatedSection>

          <AnimatedGrid className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-343" data-mp="24u8kvznyv4zs">
            {[{
            title: 'Residential Lighting',
            desc: 'Custom lighting solutions that enhance your space.',
            video: 'https://cdn.magicpatterns.com/uploads/1zqnor6CXioUFgkiR6vyAs/kling_20260618_VIDEO_Create_a_c_96_0.mp4'
          }, {
            title: 'Exterior Lighting',
            desc: 'Security and landscape lighting installations.',
            img: 'https://cdn.magicpatterns.com/uploads/jYK2h8BpxE8bw1s4qkiiCL/Screenshot_20260617_125015_Messages.jpg'
          }, {
            title: 'Panel Upgrades',
            desc: 'Safe, reliable panel upgrades built to handle your needs.',
            video: 'https://cdn.magicpatterns.com/uploads/xaXfSuzU2Y15vXS2dQxGWX/kling_20260618_VIDEO_Create_a_r_39_0.mp4'
          }, {
            title: 'Service Upgrades',
            desc: 'Complete exterior meter and panel upgrades.',
            img: 'https://cdn.magicpatterns.com/uploads/i88f8u14MjbiHBJpgzEgHA/IMG_20260617_124122.jpg'
          }, {
            title: 'Circuit Installation',
            desc: 'New circuits and organized panel labeling.',
            img: 'https://cdn.magicpatterns.com/uploads/ezaGxfSA6yhhp5w4fmLENV/IMG_20260617_124120.jpg'
          }, {
            title: 'Commercial Projects',
            desc: 'Dependable electrical solutions for your business.',
            video: 'https://cdn.magicpatterns.com/uploads/oCPmvT2JsVKoTGeBv4u8Pt/kling_20260618_VIDEO__Image1Cre_150_0.mp4'
          }, {
            title: 'Mini-Split Wiring',
            desc: 'Dedicated circuits for HVAC and mini-splits.',
            img: 'https://cdn.magicpatterns.com/uploads/fRghqUyumuhjTReGsq6KGo/IMG_20260617_124108.jpg'
          }, {
            title: 'Service Installations',
            desc: 'EV chargers, outlets, and more installed professionally.',
            video: 'https://cdn.magicpatterns.com/uploads/rKVH5qhdeF1wBqUzNwMXcu/kling_20260618_VIDEO_Create_a_r_105_0.mp4'
          }, {
            title: 'Main Panel Replacement',
            desc: 'Safe and reliable main breaker panel replacements.',
            img: 'https://cdn.magicpatterns.com/uploads/fejejfijHgvKJz95cjAD13/IMG_20260617_124116.jpg'
          }].map((project, i) => <AnimatedGridItem key={i} className="group" data-id="element-344" data-mp="iuacytp0q3xs" data-mpi={i}>
                <div className="aspect-[4/3] overflow-hidden rounded-sm mb-4 bg-slate-100" data-id="element-345" data-mp="iuk80vox71ac">
                  {project.video ? <video src={project.video} autoPlay loop muted playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-346" data-mp="ruyu3x9pkkew" /> : <img src={project.img} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-347" data-mp="2dcr7ykw9npwy" />}
                </div>
                <h3 className="font-display font-bold text-base text-accent uppercase tracking-wide mb-1" data-id="element-348" data-mp="1zlmey1nelvvl">
                  {project.title}
                </h3>
                <p className="text-sm text-slate-600 leading-snug" data-id="element-349" data-mp="1pohpnsy9hkak">
                  {project.desc}
                </p>
              </AnimatedGridItem>)}
          </AnimatedGrid>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-20 md:py-28 bg-white relative" data-id="element-350" data-mp="1gp4nzmgdq00e">
        <div className="max-w-7xl mx-auto px-6" data-id="element-351" data-mp="3f363zzqexcb8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-352" data-mp="3f2wc26lt4v2w">
            <AnimatedSection data-id="element-353" data-mp="3b2vtc9rodxq1">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-navy mb-6 tracking-tight" data-id="element-354" data-mp="3azq6l16ir7xh">
                Electrical Expertise You Can{' '}
                <span className="text-accent" data-id="element-355" data-mp="8nrn60btues7">Trust</span>
              </h2>
              <div className="w-20 h-1.5 bg-accent mb-8" data-id="element-356" data-mp="mmitv1od0y6b"></div>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-357" data-mp="mlzlocoiho5h">
                At The Electrical Professionals Inc, we are committed to
                delivering safe, dependable, and professional electrical
                services for residential and commercial customers.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-358" data-mp="3l2bqntgwtrin">
                We take pride in quality workmanship, honest communication, and
                reliable solutions that customers can trust.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-accent transition-colors group" data-id="element-359" data-mp="37cqb6t2bjufd">
                Learn More
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-360" data-mp="1hvmdr5tpq9cg" />
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="relative" data-id="element-361" data-mp="1moo01yw91b62">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-2xl relative z-10" data-id="element-362" data-mp="18whiq9hd0y51">
                <video src="https://cdn.magicpatterns.com/uploads/a2f3RJtWrdTBQeMErzZHAR/kling_20260618_VIDEO_Create_a_r_72_0.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-363" data-mp="1cxt8cf08hjd8" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-slate-100 rounded-sm z-0 border border-slate-200" data-id="element-364" data-mp="1ro0hu589js1s"></div>
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-grid-pattern opacity-10 z-0" data-id="element-365" data-mp="g5zgfaogn0so"></div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW SECTION */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-id="element-366" data-mp="2mtq2ha091a4p">
        <div className="max-w-7xl mx-auto px-6" data-id="element-367" data-mp="2hv6g58hqtwbt">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16" data-id="element-368" data-mp="2hwgfe10c1ak1">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-navy mb-6 tracking-tight" data-id="element-369" data-mp="2d3nbffcou690">
              Complete Electrical Solutions
            </h2>
            <div className="w-20 h-1.5 bg-accent mx-auto mb-6" data-id="element-370" data-mp="2440byz3irail"></div>
          </AnimatedSection>

          <AnimatedGrid className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-371" data-mp="1z5gjsgyc12hs">
            {[{
            title: 'Electrical Repairs',
            icon: Wrench,
            desc: 'Fast, reliable repairs for all electrical issues.'
          }, {
            title: 'Troubleshooting',
            icon: Activity,
            desc: 'Advanced diagnostics to find and fix hidden problems.'
          }, {
            title: 'Panel Upgrades',
            icon: Shield,
            desc: 'Modernize your electrical panel for safety and capacity.'
          }, {
            title: 'Lighting Installation',
            icon: Lightbulb,
            desc: 'Indoor, outdoor, and security lighting solutions.'
          }, {
            title: 'Commercial Electrical',
            icon: Building2,
            desc: 'Comprehensive services for businesses.'
          }, {
            title: 'Residential Electrical',
            icon: HomeIcon,
            desc: 'Complete home electrical services.'
          }, {
            title: 'Emergency Service',
            icon: AlertTriangle,
            desc: '24/7 support for urgent electrical needs.'
          }, {
            title: 'Electrical Maintenance',
            icon: Clock,
            desc: 'Preventative care to ensure reliability.'
          }].map((service, i) => <AnimatedGridItem key={i} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 hover:shadow-md transition-shadow group" data-id="element-372" data-mp="26audv9sleyol" data-mpi={i}>
                <div className="bg-slate-50 w-12 h-12 rounded-sm flex items-center justify-center mb-4 group-hover:bg-accent group-hover:text-white transition-colors" data-id="element-373" data-mp="2g90bhf7d9inn">
                  <service.icon className="w-6 h-6 text-navy group-hover:text-white transition-colors" data-id="element-374" data-mp="2u6gj2u1efigz" />
                </div>
                <h3 className="font-display text-lg font-bold text-navy mb-2" data-id="element-375" data-mp="33y34tkbd4zxz">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-376" data-mp="2z0b1zv96jtf0">
                  {service.desc}
                </p>
              </AnimatedGridItem>)}
          </AnimatedGrid>

          <AnimatedSection delay={0.3} className="text-center mt-12" data-id="element-377" data-mp="380zgss6edxsy">
            <Link to="/services" className="inline-flex items-center gap-2 bg-navy hover:bg-charcoal text-white px-8 py-4 rounded-sm font-semibold transition-colors group" data-id="element-378" data-mp="3rlzoz1f7kffz">
              View All Services
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-379" data-mp="3iki9nl9mkhsc" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <motion.section initial="dark" whileInView="light" viewport={{
      once: true,
      margin: '-100px'
    }} variants={{
      dark: {
        backgroundColor: '#020308'
      },
      light: {
        backgroundColor: '#050814',
        transition: {
          duration: 1.5,
          delay: 0.8
        }
      }
    }} className="py-20 md:py-28 text-white relative overflow-hidden" data-id="element-380" data-mp="38tnnegwdpi6w">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" data-id="element-381" data-mp="2uuxrh4lapok4"></div>

        {/* Animated Radial Glow */}
        <motion.div variants={{
        dark: {
          opacity: 0,
          scale: 0.5
        },
        light: {
          opacity: 1,
          scale: 1,
          transition: {
            duration: 2,
            delay: 0.8
          }
        }
      }} className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-yellow-400/5 rounded-full blur-3xl pointer-events-none" data-id="element-382" data-mp="10gjxziusotke"></motion.div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-383" data-mp="1eft9w6byx1ik">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-384" data-mp="1acn4t20ldgdo">
            <motion.div variants={{
            dark: {
              color: '#1e293b',
              filter: 'drop-shadow(0 0 0px rgba(250, 204, 21, 0))'
            },
            light: {
              color: ['#1e293b', '#facc15', '#1e293b', '#facc15', '#facc15'],
              filter: ['drop-shadow(0 0 0px rgba(250, 204, 21, 0))', 'drop-shadow(0 0 20px rgba(250, 204, 21, 0.8))', 'drop-shadow(0 0 0px rgba(250, 204, 21, 0))', 'drop-shadow(0 0 40px rgba(250, 204, 21, 1))', 'drop-shadow(0 0 60px rgba(250, 204, 21, 0.4))'],
              transition: {
                duration: 0.8,
                times: [0, 0.15, 0.3, 0.45, 1],
                delay: 0.4
              }
            }
          }} data-id="element-385" data-mp="15ebwivpy3dxk">
              <Lightbulb className="w-16 h-16 mx-auto mb-6" data-id="element-386" data-mp="32brdh56bd814" />
            </motion.div>

            <motion.h2 variants={{
            dark: {
              opacity: 0.3
            },
            light: {
              opacity: 1,
              transition: {
                duration: 1,
                delay: 0.8
              }
            }
          }} className="font-display text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-387" data-mp="13xy4i35q26tk">
              Built on Trust, Powered by Expertise
            </motion.h2>
            <motion.div variants={{
            dark: {
              opacity: 0,
              width: 0
            },
            light: {
              opacity: 1,
              width: 80,
              transition: {
                duration: 0.8,
                delay: 1
              }
            }
          }} className="h-1.5 bg-accent mx-auto mb-8" data-id="element-388" data-mp="tuapxv8fmn0i"></motion.div>
          </div>

          <AnimatedGrid className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-389" data-mp="1qipdors2lgyf">
            {[{
            title: 'Licensed Professionals',
            icon: Shield,
            desc: 'Fully licensed, insured, and highly trained experts.'
          }, {
            title: 'Reliable Service',
            icon: Clock,
            desc: 'We show up on time and complete projects as promised.'
          }, {
            title: 'Safety Focused',
            icon: CheckCircle2,
            desc: 'Strict adherence to all safety codes and standards.'
          }, {
            title: 'Flexible Financing',
            icon: Banknote,
            desc: 'Options available to make your project affordable.'
          }, {
            title: 'Customer Satisfaction',
            icon: Star,
            desc: "We aren't finished until you are completely satisfied."
          }, {
            title: 'Honest Communication',
            icon: ThumbsUp,
            desc: 'Transparent pricing and clear project updates.'
          }].map((feature, i) => <AnimatedGridItem key={i} className="bg-slate-800/50 border border-slate-700 p-8 rounded-sm" data-id="element-390" data-mp="vzb6yfdg5efi" data-mpi={i}>
                <feature.icon className="w-8 h-8 text-accent mb-4" data-id="element-391" data-mp="m6c7u3kbq5hn" />
                <h3 className="font-display font-bold text-xl mb-2" data-id="element-392" data-mp="r68cm50ui0y9">
                  {feature.title}
                </h3>
                <p className="text-slate-400" data-id="element-393" data-mp="c8y14vo7cv2j">{feature.desc}</p>
              </AnimatedGridItem>)}
          </AnimatedGrid>
        </div>
      </motion.section>

      {/* CTA */}
      <section className="py-20 bg-accent text-white" data-id="element-394" data-mp="392loenm6dwp">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-395" data-mp="3am4od0nn326">
          <AnimatedSection data-id="element-396" data-mp="88nk8nyvgk8w">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-397" data-mp="87w0qbijucyz">
              Need Electrical Service?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-398" data-mp="22ldghccpq5jg">
              Request Your Free Quote Today
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-399" data-mp="2cqn3v8wz6g6g">
              <Link to="/quote" className="bg-navy hover:bg-slate-800 text-white px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-400" data-mp="27r0r10l271y6">
                Request a Free Quote
              </Link>
              <a href="tel:4075040596" className="bg-white text-accent hover:bg-slate-50 px-8 py-4 rounded-sm font-bold text-lg transition-colors flex items-center justify-center gap-2" data-id="element-401" data-mp="2gpvysh3r4k3l">
                <Phone className="w-5 h-5" data-id="element-402" data-mp="34grjyvdpch1v" />
                (407) 504-0596
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}